/**
 * Lazy loader for Google Identity Services (GIS).
 *
 * T205-FE: the script used to be a tag in index.html (103 KB gzip requested at
 * ~0.3 s on the Home, pushing the entry JS from 1.96 to 2.36 s on the throttled
 * mobile profile). Nothing needs it at boot: GIS is only used by
 * `signInWithGoogle`, on a click. So the script is injected the first time
 * someone asks for it, through ONE promise shared by every caller:
 *  - the sign-in forms call `warmGoogleIdentity()` when they mount, so by the
 *    time the user clicks the script is normally there;
 *  - `signInWithGoogle` awaits `loadGoogleIdentity()`, so a click never reaches
 *    `google.accounts.*` before the script has run;
 *  - a failure (blocked, offline, no `google.accounts.id` after load, timeout)
 *    rejects with the message the old poller used and clears the shared promise,
 *    so the next click tries again instead of replaying a stale failure.
 * Pages that never show a sign-in form never download it.
 */

export const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

/** Same text the previous poller rejected with: LoginForm shows it as is. */
export const GIS_LOAD_ERROR_MESSAGE = 'Google Identity Services script not loaded (timeout)';

/**
 * Counted from the moment the script is requested (the old 5 s started with a
 * script already downloading since page load), so the budget is longer.
 */
const DEFAULT_TIMEOUT_MS = 10_000;

type GoogleGlobal = NonNullable<Window['google']>;

let inflight: Promise<GoogleGlobal> | null = null;

function present(): GoogleGlobal | undefined {
  return window.google?.accounts?.id ? window.google : undefined;
}

/** Resolves with `window.google` once GIS is usable; injects the script at most once at a time. */
export function loadGoogleIdentity(timeoutMs: number = DEFAULT_TIMEOUT_MS): Promise<GoogleGlobal> {
  const ready = present();
  if (ready) return Promise.resolve(ready);
  if (inflight) return inflight;

  const attempt = new Promise<GoogleGlobal>((resolve, reject) => {
    // An index.html cached from before T205 (service worker) may still carry the static tag:
    // listen to that element instead of requesting the 103 KB a second time.
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SCRIPT_SRC}"]`);
    const script = existing ?? document.createElement('script');
    const fail = () => {
      clearTimeout(timer);
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', fail);
      script.remove();
      reject(new Error(GIS_LOAD_ERROR_MESSAGE));
    };
    const onLoad = () => {
      clearTimeout(timer);
      const google = present();
      if (google) resolve(google);
      else fail();
    };
    script.addEventListener('load', onLoad);
    script.addEventListener('error', fail);
    const timer = setTimeout(fail, timeoutMs);
    if (!existing) {
      script.src = GIS_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });
  inflight = attempt;
  // Cleared from a handler (not inside the executor): a failure raised synchronously while the
  // script is being attached (some DOMs dispatch `error` at once) still leaves the slot empty.
  attempt.catch(() => {
    if (inflight === attempt) inflight = null;
  });
  return attempt;
}

/** Starts the download early (sign-in UI mounted) without anyone having to wait for it or handle a failure. */
export function warmGoogleIdentity(): void {
  loadGoogleIdentity().catch(() => {
    // The click that really needs GIS calls loadGoogleIdentity() again and reports its own error.
  });
}

/** Test seam: forget the shared promise. */
export function resetGoogleIdentityLoaderForTests(): void {
  inflight = null;
}
